import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Document } from '@/features/docs/Document'
import { type DocumentStore } from '@/features/docs/DocumentStore'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'

export class DeleteDocumentDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor document: Document | null = null

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _documentStore: DocumentStore,
  ) {}

  /**
   * Prompts for and performs deletion if accepted.
   *
   * @param document
   * @param signal
   * @returns A boolean promise that resolves to true if the
   *  document was deleted, or false if the operation was cancelled or
   *  the user declined.
   */
  async prompt(document: Document, signal?: AbortSignal) {
    // Close any ongoing prompt
    this.modal.close()

    // Start a new prompt
    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.document = document
      this.modal.show()
    })

    // Wait for the user to confirm or cancel
    const response = await responsePromise

    // Close the dialog
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  delete = task.resolved(async () => {
    if (!this.document) {
      return
    }

    await this._documentStore
      .archive(this.document)
      .catch(this._createSnackbarErrorHandler('Deleting failed'))
    this._prompt.respond(true)
    this.modal.close()
  })
}
